import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import './Login.css';

const ResetPasswordVerify = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [email] = useState(searchParams.get('email') || '');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [step, setStep] = useState('verify'); // 'verify' or 'reset'

  useEffect(() => {
    if (!email) {
      navigate('/forgot-password');
    }
  }, [email, navigate]);

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (code.length !== 6) {
      setError('Please enter a valid 6-digit code');
      setLoading(false);
      return;
    }

    try {
      // Verify code by attempting to use it
      const response = await api.post('/api/verify-reset-token', { token: code });
      
      if (response.data.valid) {
        setStep('reset');
        setMessage('Code verified! Please enter your new password.');
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Invalid or expired code');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    try {
      await api.post('/api/reset-password', {
        token: code,
        password: newPassword
      });

      setMessage('Password reset successful! Redirecting to login...');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        {/* Header with Cricket Logo */}
        <div className="login-header">
          <div className="cricket-logo">
            <svg width="60" height="60" viewBox="0 0 100 100" className="cricket-icon">
              <circle cx="50" cy="50" r="45" fill="#1e40af" stroke="#ffffff" strokeWidth="3"/>
              <rect x="35" y="20" width="6" height="60" fill="#ffffff" rx="3"/>
              <rect x="55" y="20" width="6" height="60" fill="#ffffff" rx="3"/>
              <rect x="45" y="20" width="6" height="60" fill="#ffffff" rx="3"/>
              <rect x="30" y="75" width="40" height="4" fill="#ffffff" rx="2"/>
              <circle cx="50" cy="35" r="8" fill="#ef4444"/>
            </svg>
          </div>
          <h1 className="academy-title">{process.env.REACT_APP_ACADEMY_NAME || 'MSKICA Cricket Academy'}</h1>
          <p className="academy-subtitle">
            {step === 'verify' ? 'Enter Verification Code' : 'Set New Password'}
          </p>
        </div>

        {/* Form Section */}
        <div className="login-form-section">
          {error && (
            <div className="error-alert">
              <span className="error-icon">⚠️</span>
              <span>{error}</span>
            </div>
          )}

          {message && (
            <div className="success-alert">
              <span className="success-icon">✅</span>
              <span>{message}</span>
            </div>
          )}

          {step === 'verify' ? (
            <form onSubmit={handleVerifyCode} className="login-form">
              <div className="form-group">
                <label className="form-label">
                  <span className="label-icon">📧</span>
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  className="form-input"
                  disabled
                />
              </div>

              <div className="form-group">
                <label htmlFor="code" className="form-label">
                  <span className="label-icon">🔢</span>
                  Verification Code
                </label>
                <input
                  type="text"
                  id="code"
                  name="code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="form-input code-input"
                  placeholder="Enter 6-digit code"
                  maxLength={6}
                  required
                />
                <small className="form-help">
                  Check your email for the 6-digit verification code
                </small>
              </div>

              <button 
                type="submit" 
                className="login-btn"
                disabled={loading || code.length !== 6}
              >
                {loading ? (
                  <>
                    <div className="spinner"></div>
                    Verifying...
                  </>
                ) : (
                  <>
                    <span>Verify Code</span>
                    <span className="btn-icon">✅</span>
                  </>
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="login-form">
              <div className="form-group">
                <label htmlFor="newPassword" className="form-label">
                  <span className="label-icon">🔒</span>
                  New Password
                </label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="form-input"
                  placeholder="Enter new password"
                  minLength={6}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword" className="form-label">
                  <span className="label-icon">🔒</span>
                  Confirm Password
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="form-input"
                  placeholder="Confirm new password"
                  minLength={6}
                  required
                />
              </div>

              <button 
                type="submit" 
                className="login-btn"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="spinner"></div>
                    Resetting...
                  </>
                ) : (
                  <>
                    <span>Reset Password</span>
                    <span className="btn-icon">🔒</span>
                  </>
                )}
              </button>
            </form>
          )}

          {/* Footer Links */}
          <div className="login-footer">
            <Link to="/login" className="footer-link">
              Remember your password? <strong>Sign In</strong>
            </Link>
            
            <Link to="/forgot-password" className="footer-link">
              Need a new code? <strong>Request again</strong>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordVerify;
